import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeams, useDeleteTeam, useToggleTeamStatus } from '../hooks/useTeams';
import { useTeamPreferences } from '../hooks';
import { TeamCard, TeamListItem } from '../components';
import { Button, DeleteConfirmationModal, FiltersButton, FilterBottomSheet, ListPageEmpty, ListPageLoading, ListPagePagination, PageToolbar, PlainHeadCell, SelectAllHeadCell, SortableHeadCell, ToolbarSearch, ViewToggle } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import type { TeamQuery, TeamResponse, ActiveStatus } from '@troia-v3/types';

type SortKey = 'name' | 'members' | 'status';

export const TeamsPage: React.FC = () => {
  const navigate = useNavigate();

  // "+" central do bottom-nav mobile → novo item (mockup .m-fab).
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); navigate('/settings/teams/new'); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, [navigate]);
  const { viewMode, setViewMode } = useTeamPreferences();
  const [searchTerm, setSearchTerm] = useState('');
  const [query, setQuery] = useState<TeamQuery>({ page: 1, limit: 12 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const { data, isLoading, error } = useTeams(query);
  const deleteTeam = useDeleteTeam();
  const toggleTeamStatus = useToggleTeamStatus();

  const items = useMemo(() => data?.items ?? [], [data]);

  // Sort client-side na página carregada (backend não expõe sort para equipes).
  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.name.localeCompare(b.name, 'pt-BR');
      else if (sort.key === 'members') cmp = (a.members?.length ?? 0) - (b.members?.length ?? 0);
      else cmp = (a.status === 'active' ? 0 : 1) - (b.status === 'active' ? 0 : 1);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sort]);

  const selectedCount = selectedIds.size;
  const allOnPageSelected = items.length > 0 && items.every((t) => selectedIds.has(t.id));
  const someOnPageSelected = items.some((t) => selectedIds.has(t.id));

  const setSearch = (term: string) => {
    setSearchTerm(term);
    setQuery((prev) => ({ ...prev, search: term || undefined, page: 1 }));
  };

  const setStatusFilter = (status?: ActiveStatus) => {
    setQuery((prev) => ({ ...prev, status, page: 1 }));
    setFiltersOpen(false);
  };

  const clearAllFilters = () => {
    setQuery((prev) => ({ ...prev, status: undefined, page: 1 }));
    setFiltersOpen(false);
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach((t) => next.delete(t.id));
      else items.forEach((t) => next.add(t.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const handleEdit = (id: string) => navigate(`/settings/teams/${id}/edit`);
  const handleManageMembers = (id: string) => navigate(`/settings/teams/${id}/edit`);

  const handleDelete = (id: string) => setDeleteId(id);

  const confirmDelete = () => {
    if (deleteId) deleteTeam.mutate(deleteId);
    setDeleteId(null);
  };

  const handleToggleStatus = (id: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    toggleTeamStatus.mutate({ id, status: newStatus });
  };

  const bulkDeactivate = () => {
    const targets = items.filter((t) => selectedIds.has(t.id) && t.status === 'active');
    targets.forEach((t) => toggleTeamStatus.mutate({ id: t.id, status: 'inactive' }));
    clearSelection();
  };

  const bulkDelete = () => setBulkDeleteOpen(true);

  const confirmBulkDelete = () => {
    Array.from(selectedIds).forEach((id) => deleteTeam.mutate(id));
    clearSelection();
    setBulkDeleteOpen(false);
  };

  const statusLabel = query.status === 'active' ? 'Ativas' : query.status === 'inactive' ? 'Inativas' : null;
  const activeFilterCount = [statusLabel].filter(Boolean).length;

  // Conteúdo dos filtros (compartilhado: popover desktop + bottom-sheet mobile)
  const filterOptions = (
    <>
      <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
      {([[undefined, 'Todas'], ['active', 'Ativas'], ['inactive', 'Inativas']] as const).map(([val, label]) => {
        const active = query.status === val;
        return (
          <button
            key={label}
            onClick={() => setStatusFilter(val)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
            data-testid={`teams-filter-status-${label}`}
          >
            {label}
            {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}
    </>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        chain="cl-tb"
        left={<>
          {/* Esquerda: breadcrumb + contador */}
          <nav className="flex items-center gap-2 min-w-0">
            <button onClick={() => navigate('/settings')} className="text-sm font-medium truncate hidden sm:inline" style={{ color: 'var(--theme-muted-text)' }}>
              Configurações
            </button>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Equipes</span>
          </nav>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {data.total}
            </span>
          )}
        </>}
        center={
          /* Centro: busca (F17-22: ToolbarSearch shared, sempre centralizada) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar equipe…"
            data-testid="teams-search"
          />
        }
        right={<>
          {/* Direita — ordem canônica (F15): Filtros → Toggle → sep → CTA */}
          {/* Filtros (F05: FiltersButton shared) */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="teams-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                >
                  {filterOptions}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-11: ViewToggle shared — Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="teams-view"
          />

          {/* .tb-sep antes do CTA */}
          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Nova equipe (único primário — F01-03: Button shared com colapso) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={() => navigate('/settings/teams/new')}
            data-testid="new-team"
          >
            Nova equipe
          </Button>
        </>}
        mobile={
          /* `.m-sub` do mockup: busca sunken + Filtros (abre bottom-sheet) */
          <>
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearch}
              placeholder="Buscar equipe…"
              data-testid="teams-search-mobile"
            />
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="teams-filters-mobile"
            >
              <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>{activeFilterCount}</span>
              )}
            </button>
          </>
        }
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusLabel}
              <button onClick={() => setStatusFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="teams-clear-filters">Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionada(s)</span>
          <span className="flex-1" />
          <button onClick={bulkDeactivate} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }} data-testid="teams-bulk-deactivate">
            <Icon name="power" className="w-3.5 h-3.5" /> Desativar
          </button>
          <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="teams-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="teams-bulk-clear">Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar equipes: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="inbox"
              message="Nenhuma equipe encontrada"
              description={searchTerm || activeFilterCount > 0 ? 'Tente ajustar a busca ou os filtros.' : 'Crie sua primeira equipe para organizar seus colaboradores'}
              actionLabel="Nova equipe"
              onAction={() => navigate('/settings/teams/new')}
            />
          )}

          {/* Grid (Cards) — F34: cards SEM checkbox, seleção em massa só na tabela */}
          {!isLoading && !error && items.length > 0 && viewMode === 'grid' && (
            <div className="grid [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))] gap-4">
              {sortedItems.map((team: TeamResponse) => (
                <TeamCard
                  key={team.id}
                  team={team}
                  members={team.members}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus} onManageMembers={handleManageMembers}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedItems.map((team: TeamResponse) => (
                  <TeamCard
                    key={team.id}
                    team={team}
                    members={team.members}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleStatus={handleToggleStatus} onManageMembers={handleManageMembers}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <SelectAllHeadCell
                        checked={allOnPageSelected}
                        indeterminate={someOnPageSelected && !allOnPageSelected}
                        onToggle={toggleSelectAllOnPage}
                        ariaLabel="Selecionar todas"
                        data-testid="teams-select-all"
                      />
                      <SortableHeadCell<SortKey> label="Equipe" sortKey="name" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell label="Descrição" />
                      <SortableHeadCell<SortKey> label="Membros" sortKey="members" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell<SortKey> label="Status" sortKey="status" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell className="w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((team: TeamResponse) => (
                      <TeamListItem
                        key={team.id}
                        team={team}
                        members={team.members}
                        selected={selectedIds.has(team.id)}
                        onToggleSelect={toggleSelect}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggleStatus={handleToggleStatus} onManageMembers={handleManageMembers}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação */}
          {!isLoading && !error && data?.page != null && data?.totalPages != null && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              totalItems={data.total}
              pageSize={data.limit}
              itemLabel="equipes"
              onPageChange={(page) => setQuery((prev) => ({ ...prev, page }))}
            />
          )}
        </div>
      </div>

      {/* Bottom-sheet de filtros (mobile) — reusa o mesmo conteúdo do popover */}
      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="teams-filters-sheet">
        {filterOptions}
      </FilterBottomSheet>

      {/* Confirmação de exclusão (single) */}
      <DeleteConfirmationModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        message="Tem certeza que deseja deletar esta equipe?"
        isLoading={deleteTeam.isPending}
      />

      {/* Confirmação de exclusão (bulk) */}
      <DeleteConfirmationModal
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={confirmBulkDelete}
        message={`Excluir ${selectedCount} equipe(s) selecionada(s)?`}
        isLoading={deleteTeam.isPending}
      />
    </div>
  );
};

TeamsPage.displayName = 'TeamsPage';
